// Dates and times as the design system prints them ("Sep 11, 2026", "9:30 AM EDT"), in a fixed zone so a server
// render and a browser render agree, and assembled from Intl parts with plain spaces (ICU's narrow no-break space
// before AM/PM varies by runtime). relativeAge reads the frozen instant (useNow), never the clock.

import { TIME_TEMPLATES } from "./labels";

export type Instant = number | string | Date;

/** The demo world and the alt captures are on US Eastern time; a caller passes the event or viewer zone. */
export const DEFAULT_TIME_ZONE = "America/New_York";

const DAY = 86_400_000;

/** The printed shapes, for the SSR allow-list and the rendered-word scan (a date or time is data, not a label). */
export const DATE_TEXT = /\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{1,2}, \d{4}\b/;
export const TIME_TEXT = /\b\d{1,2}:\d{2} (?:AM|PM)(?: (?:[A-Z]{2,5}|GMT[+-]\d{1,2}(?::\d{2})?))?(?![\w+-])/;

export function toEpoch(at: Instant): number {
  return typeof at === "number" ? at : typeof at === "string" ? Date.parse(at) : at.getTime();
}

function parts(at: Instant, options: Intl.DateTimeFormatOptions): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-US", options).formatToParts(new Date(toEpoch(at)))) out[part.type] = part.value;
  return out;
}

/** A date-only value ("2026-11-12": a start date, a due date) names a day, not a moment. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * "Sep 11, 2026". A date-only string is printed as the day it names, in no zone (F-168): read as an instant it is UTC
 * midnight, which a western zone printed as the day before ("2026-11-12" as "Nov 11").
 */
export function formatDate(at: Instant, timeZone: string = DEFAULT_TIME_ZONE): string {
  const day = typeof at === "string" ? DATE_ONLY.exec(at.trim()) : null;
  const p = day
    ? parts(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3])), { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" })
    : parts(at, { timeZone, month: "short", day: "numeric", year: "numeric" });
  return `${p.month} ${p.day}, ${p.year}`;
}

/** "9:30 AM", or "9:30 AM EDT" with the zone. */
export function formatTime(at: Instant, options: { timeZone?: string; zone?: boolean } = {}): string {
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;
  const p = parts(at, { timeZone, hour: "numeric", minute: "2-digit", hour12: true, ...(options.zone ? { timeZoneName: "short" } : {}) });
  const time = `${p.hour}:${p.minute} ${(p.dayPeriod ?? "").toUpperCase()}`;
  return options.zone && p.timeZoneName ? `${time} ${p.timeZoneName}` : time;
}

/** "Sep 11, 2026 · 9:30 AM EDT": the absolute time a stamp's tooltip carries. */
export function formatDateTime(at: Instant, timeZone: string = DEFAULT_TIME_ZONE): string {
  return `${formatDate(at, timeZone)} · ${formatTime(at, { timeZone, zone: true })}`;
}

/**
 * How long ago `at` was, seen from `now` (the frozen instant): "{n} days ago" from two whole days, his words (DS #79);
 * under one day the time, at one day (or in the future) the date, because he has no other relative words.
 */
export function relativeAge(now: Instant, at: Instant, timeZone: string = DEFAULT_TIME_ZONE): string {
  const elapsed = toEpoch(now) - toEpoch(at);
  const days = Math.floor(elapsed / DAY);
  if (days >= 2) return TIME_TEMPLATES.daysAgo({ n: days });
  if (elapsed >= 0 && days === 0) return formatTime(at, { timeZone });
  return formatDate(at, timeZone);
}
